import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Star, Plane, Building2, X, MapPin, MapPinned, CalendarDays, Newspaper, BookmarkPlus, ExternalLink, Trash2, Pencil, Check, ShieldCheck, MessageSquareWarning, BookOpen, BellRing, Images } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Spinner, IataBadge } from "../components/common";
import { airlineLogo, pickAirportBg } from "../lib/assets";
import Weather from "../components/Weather";
import InviteFriends from "../components/InviteFriends";
import NewsFeed from "../components/NewsFeed";
import CityPicker from "../components/travel/CityPicker";
import AddFavoriteModal from "../components/travel/AddFavoriteModal";
import TravelNotes from "../components/travel/TravelNotes";
import HubExpenses from "../components/travel/HubExpenses";
import WorldClock from "../components/travel/WorldClock";
import CurrencyConverter from "../components/travel/CurrencyConverter";
import NearbyAirports from "../components/travel/NearbyAirports";
import FlightStatus from "../components/travel/FlightStatus";
import SavedFlights from "../components/travel/SavedFlights";
import FlightAlerts from "../components/travel/FlightAlerts";
import RemindMeSection from "../components/travel/RemindMeSection";
import TravelHubLanding from "./TravelHubLanding";
import TripsSection from "../components/travel/TripsSection";
import TodayCard from "../components/travel/TodayCard";
import ImportGuide from "../components/travel/ImportGuide";
import JourneySection from "../components/travel/JourneySection";
import MemoirsSection from "../components/travel/MemoirsSection";
import FoodListSection from "../components/travel/FoodListSection";
import GearSection from "../components/travel/GearSection";
import TravelersManager from "../components/travel/TravelersManager";
import HouseholdManager from "../components/travel/HouseholdManager";
import MembershipBanner from "../components/travel/MembershipBanner";
import TravelStats from "../components/travel/TravelStats";
import LoyaltyManager from "../components/travel/LoyaltyManager";
import BenefitsWallet from "../components/wallet/BenefitsWallet";
import VisaLinks from "../components/travel/VisaLinks";
import InsuranceManager from "../components/travel/InsuranceManager";
import BaggageManager from "../components/travel/BaggageManager";
import HubIntro from "../components/travel/HubIntro";
import GuardianSection from "../components/travel/GuardianSection";
import HomeZoneCard from "../components/travel/HomeZoneCard";
import { AppTopBar } from "../components/AppScreen";

const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; };

/* Live countdown to the next upcoming trip. */
function TripCountdown() {
  const [next, setNext] = useState(null);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    api.get("/trips").then((r) => {
      const today = new Date().toISOString().slice(0, 10);
      const up = (r.data.items || []).filter((t) => t.start_date && t.start_date >= today)
        .sort((a, b) => a.start_date.localeCompare(b.start_date));
      setNext(up[0] || null);
    }).catch(() => {});
    const iv = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(iv);
  }, []);
  if (!next) return null;
  const target = new Date(`${next.start_date}T00:00:00`);
  const diff = Math.max(0, target - now);
  const d = Math.floor(diff / 86400000), h = Math.floor((diff % 86400000) / 3600000),
    m = Math.floor((diff % 3600000) / 60000), s = Math.floor((diff % 60000) / 1000);
  return (
    <Link to={`/trips/${next.id}`} data-testid="trip-countdown"
      className="flex flex-wrap items-center gap-4 border border-cyan-300 bg-gradient-to-r from-cyan-50 to-blue-50 px-5 py-4 hover:border-cyan-500 transition-colors">
      <Plane className="w-8 h-8 text-cyan-600 -rotate-45 shrink-0" />
      <div className="min-w-0">
        <div className="text-[11px] font-mono uppercase tracking-wider text-cyan-700">Next trip</div>
        <div className="font-heading font-900 text-lg truncate">{next.title}</div>
        <div className="text-xs text-muted-foreground">{next.start_date}{next.stops?.length ? ` · ${next.stops.join(" → ")}` : ""}</div>
      </div>
      <div className="ml-auto flex gap-2 text-center" data-testid="trip-countdown-timer">
        {[[d, "days"], [h, "hrs"], [m, "min"], [s, "sec"]].map(([v, l]) => (
          <div key={l} className="bg-white border border-cyan-200 px-2.5 py-1.5 min-w-[54px]">
            <div className="font-heading font-900 text-xl tabular-nums text-cyan-700">{v}</div>
            <div className="text-[9px] font-mono uppercase text-muted-foreground">{l}</div>
          </div>
        ))}
      </div>
    </Link>
  );
}

/* Jump-to-section bookmarks. flex-wrap so on mobile they flow into 2-3 lines
   instead of hiding off-screen in a horizontal scroller. */
const SECTIONS = [
  ["hub-favorites", "Favorites"],
  ["hub-flight-status", "Flight Status"],
  ["hub-saved-flights", "Saved Flights"],
  ["hub-alerts", "Alerts"],
  ["hub-remindme", "Remind Me"],
  ["hub-trips", "Trips"],
  ["hub-guardian", "Travel Guardian"],
  ["hub-expenses", "Expense Tracker"],
  ["hub-food", "Food List"],
  ["hub-journeys", "Travel Diaries"],
  ["hub-gear", "Luggage & Gear"],
  ["hub-travellers", "Travellers"],
  ["hub-stats", "Map & Stats"],
  ["hub-wallet", "Benefits Wallet"],
  ["hub-visas", "Visas"],
  ["hub-insurance", "Insurance"],
  ["hub-baggage", "My Baggage"],
  ["hub-notes", "Notes"],
  ["hub-tools", "Reading List & Tools"],
  ["hub-news", "News"],
];

function SectionNav({ order, hidden, onReorder, onToggleHide }) {
  const [editMode, setEditMode] = useState(false);
  const [top, setTop] = useState(0);
  const dragFrom = { current: null };
  useEffect(() => { setTop(document.querySelector("header")?.offsetHeight || 0); }, []);
  const labels = Object.fromEntries(SECTIONS);
  const go = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const navH = document.getElementById("hub-section-nav-bar")?.offsetHeight || 0;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - top - navH - 8, behavior: "smooth" });
  };
  const move = (i, dir) => {
    const next = [...order]; const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    onReorder(next);
  };
  const drop = (i) => { const from = dragFrom.current; if (from == null || from === i) return; const next = [...order]; const [m] = next.splice(from, 1); next.splice(i, 0, m); onReorder(next); };
  return (
    <div id="hub-section-nav-bar" className="sticky z-30 bg-[#fafafa]/95 backdrop-blur border-b border-zinc-200 -mx-5 lg:-mx-8 px-5 lg:px-8 py-2" style={{ top }}>
      <nav className="flex items-center gap-1.5 flex-nowrap overflow-x-auto md:flex-wrap md:overflow-visible pb-0.5" data-testid="hub-section-nav" aria-label="Jump to section">
        {order.map((id, i) => {
          const isHidden = hidden.includes(id);
          if (isHidden && !editMode) return null;
          return (
          <span key={id} className="inline-flex items-stretch shrink-0">
            {editMode && <button onClick={() => move(i, -1)} data-testid={`hub-nav-left-${id}`} className="px-1 border border-r-0 border-amber-300 bg-amber-50 text-amber-700 text-xs">‹</button>}
            <button onClick={() => (editMode ? onToggleHide(id) : go(id))} data-testid={`hub-nav-${id}`}
              draggable={editMode}
              onDragStart={() => { dragFrom.current = i; }}
              onDragOver={(e) => editMode && e.preventDefault()}
              onDrop={() => editMode && drop(i)}
              title={editMode ? (isHidden ? "Click to show this section again" : "Click to hide this section") : undefined}
              className={`px-2.5 py-1.5 text-xs font-600 border transition-colors ${editMode ? (isHidden ? "border-zinc-300 bg-zinc-100 text-zinc-400 line-through cursor-pointer" : "border-amber-300 bg-amber-50 text-amber-800 cursor-grab") : "border-zinc-300 bg-white text-zinc-700 hover:border-primary hover:text-primary"}`}>
              {labels[id]}
            </button>
            {editMode && <button onClick={() => move(i, 1)} data-testid={`hub-nav-right-${id}`} className="px-1 border border-l-0 border-amber-300 bg-amber-50 text-amber-700 text-xs">›</button>}
          </span>
          );
        })}
        <button onClick={() => setEditMode((m) => !m)} data-testid="hub-nav-reorder"
          className={`ml-auto shrink-0 px-2.5 py-1.5 text-xs font-700 border ${editMode ? "bg-emerald-600 text-white border-emerald-600" : "border-dashed border-zinc-400 text-muted-foreground hover:text-primary hover:border-primary"}`}>
          {editMode ? "Done" : "Reorder"}
        </button>
      </nav>
      {editMode && <p className="text-[11px] text-amber-700 mt-1">Drag a tab (or use ‹ ›) to reorder sections. Click a tab to hide/show it. Saved to your account instantly.</p>}
    </div>
  );
}

/* Read-only awareness for free/expired members: the hub stays browsable —
   nothing is hidden — with a clear path to see what's inside + plans. */
function ReadOnlyBanner() {
  const [state, setState] = useState(null);
  useEffect(() => { api.get("/memberships/me").then((r) => setState(r.data)).catch(() => {}); }, []);
  if (!state?.read_only) return null;
  return (
    <div className="mt-2 flex flex-wrap items-center gap-3 border border-amber-300 bg-amber-50 px-4 py-2.5" data-testid="hub-readonly-banner">
      <ShieldCheck className="w-4 h-4 text-amber-700 shrink-0" />
      <p className="text-sm text-zinc-800 min-w-0">
        <b>Your free trial has ended — the hub is now read-only.</b> Everything you created is safe and
        you can browse it all.{" "}
        <Link to="/travel-hub-landing" className="text-blue-700 font-700 hover:underline" data-testid="hub-readonly-inside">See what's inside</Link>
        {" · "}
        <Link to="/membership" className="text-blue-700 font-700 hover:underline" data-testid="hub-readonly-upgrade">View membership plans</Link>
      </p>
    </div>
  );
}

/* First-visit cross-link to the "MTH — What & Why?" article; dismiss persists locally. */
function GuideBanner() {  const [hidden, setHidden] = useState(() => localStorage.getItem("tp_mth_guide_dismissed") === "1");
  if (hidden) return null;
  return (
    <div className="mt-2 flex items-center gap-3 border border-blue-200 bg-blue-50/70 px-4 py-2.5" data-testid="hub-guide-banner">
      <BookOpen className="w-4 h-4 text-blue-700 shrink-0" />
      <p className="text-sm text-zinc-700 min-w-0">
        <b>New to My Travel Hub?</b> See what it does and why your data stays private —{" "}
        <Link to="/my-travel-hub-guide" className="text-blue-700 font-700 hover:underline" data-testid="hub-guide-link">
          read "MTH — What &amp; Why?"
        </Link>
      </p>
      <button onClick={() => { localStorage.setItem("tp_mth_guide_dismissed", "1"); setHidden(true); }}
        title="Dismiss" data-testid="hub-guide-dismiss" className="ml-auto text-zinc-400 hover:text-zinc-700 shrink-0">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function TravelHub() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [addType, setAddType] = useState(null);
  const [changingCity, setChangingCity] = useState(false);
  const [editProfile, setEditProfile] = useState(false);
  const [savedFlightsKey, setSavedFlightsKey] = useState(0);

  const load = useCallback(() => api.get("/travel/hub").then((r) => setData(r.data)).catch(() => setData(null)), []);
  useEffect(() => { if (user) load(); }, [user, load]);

  if (authLoading) return <Spinner full />;
  if (!user) return <TravelHubLanding />;
  if (!data) return <Spinner full />;

  const { profile, prefs, favorite_airports, favorite_airlines, notes, upcoming } = data;
  const savedLinks = prefs.saved_links || [];

  const savePrefs = async (patch) => { const r = await api.put("/travel/prefs", patch); setData((d) => ({ ...d, prefs: r.data })); };
  const pickCity = async (r) => { await savePrefs({ home_city: r.name, home_country: r.country, home_lat: r.lat, home_lon: r.lon, home_timezone: r.timezone }); setChangingCity(false); toast.success(`Home set to ${r.name}`); };
  const resetCity = async () => savePrefs({ home_city: null, home_country: null, home_lat: null, home_lon: null, home_timezone: null });
  const removeFav = async (id) => { await api.delete(`/travel/favorites/${id}`); load(); };

  const HDR = pickAirportBg(user.user_id);
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" });

  // Custom section order: saved ids first (valid ones), then any new sections appended.
  const DEFAULT_ORDER = SECTIONS.map(([id]) => id);
  const saved = (prefs.hub_section_order || []).filter((id) => DEFAULT_ORDER.includes(id));
  const order = [...saved, ...DEFAULT_ORDER.filter((id) => !saved.includes(id))];
  const setOrder = (next) => savePrefs({ hub_section_order: next });
  const hidden = (prefs.hub_hidden_sections || []).filter((id) => DEFAULT_ORDER.includes(id));
  const toggleHide = (id) => savePrefs({ hub_hidden_sections: hidden.includes(id) ? hidden.filter((x) => x !== id) : [...hidden, id] });

  const blocks = {
    "hub-favorites": (
      <div id="hub-favorites" className="scroll-mt-24 grid lg:grid-cols-2 gap-10">
        <FavBlock title="My Favorite Airports" icon={<Building2 className="w-6 h-6" />} color="text-blue-600" items={favorite_airports}
          onAdd={() => setAddType("airport")} onRemove={removeFav} kind="airport" />
        <FavBlock title="My Favorite Airlines" icon={<Plane className="w-6 h-6 -rotate-45" />} color="text-rose-600" items={favorite_airlines}
          onAdd={() => setAddType("airline")} onRemove={removeFav} kind="airline" />
      </div>
    ),
    "hub-flight-status": <div id="hub-flight-status" className="scroll-mt-24"><FlightStatus onSaved={() => setSavedFlightsKey((k) => k + 1)} /></div>,
    "hub-saved-flights": <div id="hub-saved-flights" className="scroll-mt-24"><SavedFlights refreshKey={savedFlightsKey} /></div>,
    "hub-alerts": <div id="hub-alerts" className="scroll-mt-24 space-y-12"><FlightAlerts /><TodayCard /></div>,
    "hub-remindme": <div id="hub-remindme" className="scroll-mt-24"><RemindMeSection /></div>,
    "hub-trips": (
      <div id="hub-trips" className="scroll-mt-24 space-y-12">
        <TripsSection />
        <ImportGuide />
        {upcoming.length > 0 && (
          <div>
            <h2 className="font-heading font-900 text-cyan-600 text-2xl mb-4 flex items-center gap-2"><CalendarDays className="w-6 h-6" /> Upcoming Travel</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {upcoming.map((n) => (
                <div key={n.id} className="border border-zinc-300 bg-white p-4 flex items-center gap-3" data-testid={`upcoming-${n.id}`}>
                  <div className="text-center shrink-0">
                    <div className="font-mono text-xs text-muted-foreground uppercase">{new Date(n.trip_date).toLocaleDateString("en-US", { month: "short" })}</div>
                    <div className="font-heading font-900 text-2xl text-primary leading-none">{new Date(n.trip_date).getDate()}</div>
                  </div>
                  <div className="min-w-0">
                    <div className="font-600 text-sm truncate">{n.title}</div>
                    <div className="text-xs text-muted-foreground truncate">{n.dep || "?"} → {n.arr || "?"}{n.flight ? ` · ${n.airline || ""} ${n.flight}` : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    ),
    "hub-food": <div id="hub-food" className="scroll-mt-24"><FoodListSection /></div>,
    "hub-guardian": <div id="hub-guardian" className="scroll-mt-24"><GuardianSection /></div>,
    "hub-journeys": <div id="hub-journeys" className="scroll-mt-24 space-y-12"><JourneySection /><MemoirsSection /></div>,
    "hub-expenses": <div id="hub-expenses" className="scroll-mt-24"><HubExpenses /></div>,
    "hub-gear": <div id="hub-gear" className="scroll-mt-24"><GearSection /></div>,
    "hub-travellers": <div id="hub-travellers" className="scroll-mt-24 space-y-12"><TravelersManager /><HouseholdManager /></div>,
    "hub-stats": <div id="hub-stats" className="scroll-mt-24"><TravelStats /></div>,
    "hub-wallet": <div id="hub-wallet" className="scroll-mt-24"><BenefitsWallet /></div>,
    "hub-visas": <div id="hub-visas" className="scroll-mt-24"><VisaLinks /></div>,
    "hub-insurance": <div id="hub-insurance" className="scroll-mt-24"><InsuranceManager /></div>,
    "hub-baggage": <div id="hub-baggage" className="scroll-mt-24"><BaggageManager /></div>,
    "hub-notes": <div id="hub-notes" className="scroll-mt-24"><TravelNotes notes={notes} onChange={load} /></div>,
    "hub-tools": (
      <div id="hub-tools" className="scroll-mt-24 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        <ReadLater links={savedLinks} onChange={(l) => savePrefs({ saved_links: l })} />
        <CurrencyConverter />
        <WorldClock cities={prefs.world_clock || []} onChange={(c) => savePrefs({ world_clock: c })} />
        <NearbyAirports lat={prefs.home_lat} lon={prefs.home_lon} cityName={prefs.home_city} />
      </div>
    ),
    "hub-news": (
      <div id="hub-news" className="scroll-mt-24">
        <h2 className="font-heading font-900 text-violet-600 text-2xl mb-4 flex items-center gap-2"><Newspaper className="w-6 h-6" /> Travel News For You</h2>
        {favorite_airlines[0] || favorite_airports[0] ? (
          <div className="grid lg:grid-cols-2 gap-8">
            {favorite_airlines[0] && (<div><div className="text-sm font-600 mb-2">{favorite_airlines[0].name}</div><NewsFeed path={`/news?category=airline&q=${encodeURIComponent(favorite_airlines[0].name)}&limit=4`} compact /></div>)}
            {favorite_airports[0] && (<div><div className="text-sm font-600 mb-2">{favorite_airports[0].name}</div><NewsFeed path={`/news?category=airport&q=${encodeURIComponent(favorite_airports[0].name)}&limit=4`} compact /></div>)}
          </div>
        ) : <p className="text-sm text-muted-foreground">Add favorite airports & airlines to get personalized news here.</p>}
      </div>
    ),
  };

  return (
    <div data-testid="travel-hub" className="overflow-x-hidden">
      <AppTopBar title="Trips" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 pt-3">
        <MembershipBanner />
        <GuideBanner />
        <ReadOnlyBanner />
      </div>
      {/* Header */}
      <section className="relative border-b border-zinc-200">
        <div className="absolute inset-0"><img src={HDR} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-indigo-900/80" /></div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-10">
          <div className="text-xs font-mono uppercase tracking-widest text-blue-300 mb-3">✈️ My Travel Hub — everything for your journey, in one place</div>
          <div className="flex flex-col md:flex-row md:items-center gap-5">
            <div className="flex items-center gap-4">
              {profile.picture
                ? <img src={profile.picture} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-white/30" />
                : <div className="w-16 h-16 rounded-full bg-primary text-white flex items-center justify-center font-heading font-900 text-2xl">{(profile.name || "U")[0].toUpperCase()}</div>}
              <div>
                {editProfile ? (
                  <ProfileEditor profile={profile} onSave={async (p) => { await savePrefs(p); setEditProfile(false); load(); }} onCancel={() => setEditProfile(false)} />
                ) : (
                  <>
                    <div className="text-white font-heading font-900 text-2xl sm:text-3xl flex items-center gap-2">
                      {greeting()}, {profile.name?.split(" ")[0] || "traveller"}!
                      <button onClick={() => setEditProfile(true)} data-testid="edit-profile" className="text-white/60 hover:text-white"><Pencil className="w-4 h-4" /></button>
                    </div>
                    <div className="text-white/70 text-sm">Ready for your next journey?</div>
                  </>
                )}
              </div>
            </div>
            <div className="md:ml-auto text-white">
              <div className="flex items-center gap-2 text-sm text-white/80"><CalendarDays className="w-4 h-4" /> {today}</div>
              <div className="flex items-center gap-1.5 mt-1 text-lg font-500"><MapPin className="w-4 h-4" />{prefs.home_city || "No home city set"}</div>
              {prefs.home_lat != null && <div className="mt-2"><Weather lat={prefs.home_lat} lon={prefs.home_lon} label={prefs.home_city} /></div>}
              <div className="mt-2 flex gap-3 text-xs">
                <button onClick={() => setChangingCity((c) => !c)} data-testid="change-city" className="text-blue-300 hover:text-white">Change city</button>
                {prefs.home_city && <button onClick={resetCity} data-testid="reset-city" className="text-blue-300 hover:text-white">Reset</button>}
              </div>
            </div>
          </div>
          {changingCity && (
            <div className="mt-4 max-w-md bg-white p-3 border border-zinc-300"><CityPicker autoFocus placeholder="Set your home city…" onSelect={pickCity} /></div>
          )}
          <div className="mt-4 flex flex-nowrap overflow-x-auto md:flex-wrap md:overflow-visible gap-2 pb-1">
            <Link to="/plan-ai" data-testid="hub-ai-planner" className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 bg-emerald-500/90 text-white border border-emerald-300/50 px-3 py-1.5 text-sm font-700 hover:bg-emerald-500"><Star className="w-4 h-4" /> Plan a trip with AI</Link>
            <Link to="/security-privacy" data-testid="hub-security-article-top" className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 bg-white/10 text-white border border-white/20 px-3 py-1.5 text-sm hover:bg-white/20"><ShieldCheck className="w-4 h-4" /> Security &amp; Privacy at My Travel Hub</Link>
            <Link to="/my-photos" data-testid="hub-my-photos" className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 bg-white/10 text-white border border-white/20 px-3 py-1.5 text-sm hover:bg-white/20"><Images className="w-4 h-4" /> My Photos</Link>
            <Link to="/my-stories" data-testid="hub-my-stories" className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 bg-white/10 text-white border border-white/20 px-3 py-1.5 text-sm hover:bg-white/20"><BookOpen className="w-4 h-4" /> My Travel Stories</Link>
            <Link to="/photo-map" data-testid="hub-photo-map" className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 bg-white/10 text-white border border-white/20 px-3 py-1.5 text-sm hover:bg-white/20"><MapPinned className="w-4 h-4" /> Photo Map</Link>
            <Link to="/memoirs" data-testid="hub-memoirs" className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 bg-white/10 text-white border border-white/20 px-3 py-1.5 text-sm hover:bg-white/20"><BookOpen className="w-4 h-4" /> My Memoirs</Link>
            <Link to="/community/saved" data-testid="hub-saved-posts" className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 bg-white/10 text-white border border-white/20 px-3 py-1.5 text-sm hover:bg-white/20"><BookmarkPlus className="w-4 h-4" /> Saved posts</Link>
            <Link to="/my-deal-alerts" data-testid="hub-deal-alerts" className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 bg-white/10 text-white border border-white/20 px-3 py-1.5 text-sm hover:bg-white/20"><BellRing className="w-4 h-4" /> Deal alerts</Link>
            <Link to="/dashboard" data-testid="hub-complaints" className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 bg-white/10 text-white border border-white/20 px-3 py-1.5 text-sm hover:bg-white/20"><MessageSquareWarning className="w-4 h-4" /> My complaints</Link>
            <Link to="/feedback?from=/travel-hub" data-testid="hub-feedback" className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 bg-white/10 text-white border border-white/20 px-3 py-1.5 text-sm hover:bg-white/20"><MessageSquareWarning className="w-4 h-4" /> Feedback &amp; suggestions</Link>
            {["moderator", "admin"].includes(user.role) && <Link to="/moderation" className="shrink-0 whitespace-nowrap inline-flex items-center gap-1.5 bg-white/10 text-white border border-white/20 px-3 py-1.5 text-sm hover:bg-white/20"><ShieldCheck className="w-4 h-4" /> Moderation</Link>}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10 space-y-12">
        <SectionNav order={order} hidden={hidden} onReorder={setOrder} onToggleHide={toggleHide} />
        <Link to="/plan-ai" data-testid="hub-ai-planner-banner"
          className="flex items-center gap-4 border border-emerald-300 bg-gradient-to-r from-emerald-50 to-teal-50 px-5 py-4 hover:border-emerald-500 transition-colors">
          <div className="w-10 h-10 shrink-0 bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center"><Star className="w-5 h-5 text-white" /></div>
          <div className="min-w-0">
            <div className="font-heading font-900 text-emerald-800">AI Trip Planner — describe it, we'll plan it</div>
            <div className="text-xs text-muted-foreground truncate">"10 days in Japan under $5000" → a complete day-by-day itinerary you can edit &amp; save.</div>
          </div>
          <span className="ml-auto shrink-0 flex flex-col items-end gap-1">
            <span className="text-sm font-700 text-emerald-700">Start →</span>
            <span onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate("/plan-ai?surprise=1"); }}
              data-testid="hub-surprise-me" role="button"
              className="text-[11px] font-700 text-violet-700 bg-violet-50 border border-violet-300 px-2 py-0.5 hover:bg-violet-100">
              🎲 Surprise me
            </span>
          </span>
        </Link>
        <TripCountdown />
        <HomeZoneCard />
        {order.filter((id) => !hidden.includes(id)).map((id) => <span key={id} className="contents">{blocks[id]}</span>)}

        {/* About My Travel Hub */}
        <details className="border border-zinc-300 bg-white group" data-testid="hub-about">
          <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between font-heading font-700 text-lg">
            About My Travel Hub
            <span className="text-sm font-400 text-primary group-open:hidden">Read more ↓</span>
          </summary>
          <div className="px-5 pb-6"><HubIntro /></div>
        </details>

        {/* Invite friends (QR + WhatsApp with What/Why + MTH links) */}
        <InviteFriends />

        {/* Security & Privacy article link (bottom) */}
        <Link to="/security-privacy" data-testid="hub-security-article-bottom" className="block border border-zinc-200 bg-white hover:border-[#4285F4] transition-colors group">
          <div className="h-1 flex">{["#4285F4", "#EA4335", "#F9AB00", "#34A853"].map((c) => <div key={c} className="flex-1" style={{ background: c }} />)}</div>
          <div className="p-5 flex items-center gap-4">
            <ShieldCheck className="w-8 h-8 shrink-0" style={{ color: "#4285F4" }} />
            <div className="flex-1 min-w-0">
              <div className="font-heading font-900 text-lg">Your Travel Information Is Personal. We're Working to Keep It Protected.</div>
              <div className="text-sm text-muted-foreground">Read how Security &amp; Privacy is built into My Travel Hub — encryption, private-by-default storage, data minimization, responsible AI and your controls.</div>
            </div>
            <span className="text-sm font-600 shrink-0 group-hover:underline" style={{ color: "#4285F4" }}>Read the article →</span>
          </div>
        </Link>
      </div>

      {addType && <AddFavoriteModal type={addType} onClose={() => setAddType(null)} onAdded={() => { load(); }} />}
    </div>
  );
}

function FavBlock({ title, icon, color, items, onAdd, onRemove, kind }) {
  return (
    <div data-testid={`fav-block-${kind}`}>
      <div className="flex items-center justify-between mb-4">
        <h2 className={`font-heading font-900 text-2xl flex items-center gap-2 ${color}`}>{icon} {title} <span className="text-sm font-500 text-muted-foreground">({items.length}/20)</span></h2>
        <button onClick={onAdd} disabled={items.length >= 20} data-testid={`fav-add-${kind}`} className="inline-flex items-center gap-1.5 px-3 py-2 bg-primary text-white text-sm font-600 disabled:opacity-50 hover:bg-blue-700 transition-colors"><Star className="w-4 h-4" /> Add</button>
      </div>
      {items.length === 0 ? <p className="text-sm text-muted-foreground">No favorites yet — add up to 20.</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {items.map((f) => (
            <div key={f.id} className="relative group border border-zinc-300 bg-white overflow-hidden" data-testid={`fav-${kind}-${f.entity_id}`}>
              <Link to={f.link}>
                <IataBadge code={f.code || "—"} name={f.name} logo={kind === "airline" ? airlineLogo(f.code) : null} className="h-24" size="text-2xl" />
              </Link>
              <div className="p-2">
                <div className="text-xs text-muted-foreground truncate flex items-center gap-1"><MapPin className="w-3 h-3" />{f.city || f.country || "—"}</div>
                {kind === "airport" && f.lat != null && <div className="mt-1 text-primary"><Weather lat={f.lat} lon={f.lon} compact /></div>}
              </div>
              <button onClick={() => onRemove(f.id)} data-testid={`fav-remove-${f.id}`} className="absolute top-1 right-1 bg-white/90 p-1 opacity-0 group-hover:opacity-100 hover:text-destructive transition-opacity"><X className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProfileEditor({ profile, onSave, onCancel }) {
  const [name, setName] = useState(profile.name || "");
  const [pic, setPic] = useState(profile.picture || "");
  return (
    <div className="bg-white p-3 border border-zinc-300 w-72" data-testid="profile-editor">
      <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Display name" data-testid="profile-name" className="w-full border border-zinc-300 px-2 py-1.5 text-sm mb-2 outline-none focus:ring-2 focus:ring-primary" />
      <input value={pic} onChange={(e) => setPic(e.target.value)} placeholder="Photo URL (optional)" data-testid="profile-pic" className="w-full border border-zinc-300 px-2 py-1.5 text-sm mb-2 outline-none focus:ring-2 focus:ring-primary" />
      <div className="flex gap-2 justify-end">
        <button onClick={onCancel} className="px-3 py-1.5 border border-zinc-300 text-sm">Cancel</button>
        <button onClick={() => onSave({ display_name: name, photo_override: pic || null })} data-testid="profile-save" className="px-3 py-1.5 bg-primary text-white text-sm inline-flex items-center gap-1"><Check className="w-4 h-4" /> Save</button>
      </div>
    </div>
  );
}

function ReadLater({ links, onChange }) {
  const [title, setTitle] = useState("");
  const [url, setUrl] = useState("");
  const add = () => {
    if (!title.trim() || !url.trim()) return;
    const u = url.startsWith("http") ? url : `https://${url}`;
    onChange([{ title, url: u }, ...links]); setTitle(""); setUrl("");
  };
  const remove = (i) => onChange(links.filter((_, idx) => idx !== i));
  return (
    <div className="border border-zinc-300 bg-white p-5" data-testid="read-later">
      <h3 className="font-heading font-900 text-orange-600 text-lg flex items-center gap-2 mb-3"><BookmarkPlus className="w-5 h-5" /> Read Later</h3>
      <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title" data-testid="readlater-title" className="w-full border border-zinc-300 px-2 py-1.5 text-sm mb-2 outline-none focus:ring-2 focus:ring-primary" />
      <div className="flex gap-2 mb-3">
        <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Paste a link…" data-testid="readlater-url" className="flex-1 border border-zinc-300 px-2 py-1.5 text-sm outline-none focus:ring-2 focus:ring-primary" />
        <button onClick={add} data-testid="readlater-add" className="px-3 py-1.5 bg-primary text-white text-sm">Save</button>
      </div>
      {links.length === 0 ? <p className="text-sm text-muted-foreground">Save articles & links to read later.</p> : (
        <div className="space-y-2">
          {links.map((l, i) => (
            <div key={`${l.url}-${i}`} className="flex items-center gap-2 group" data-testid={`readlater-${i}`}>
              <a href={l.url} target="_blank" rel="noreferrer" className="flex-1 min-w-0 text-sm text-primary hover:underline truncate inline-flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5 shrink-0" />{l.title}</a>
              <button onClick={() => remove(i)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
